import { useEffect, useCallback, type ChangeEvent } from 'react';
import { useGame } from '../context/GameContext';
import { useLocation } from '../hooks/useLocation';
import { useTheme } from '../hooks/useTheme';
import { DemoBanner } from '../components/DemoBanner';
import { ThemeToggle } from '../components/ThemeToggle';
import { ScoreBoard } from '../components/ScoreBoard';
import { PlateSelector } from '../components/PlateSelector';
import { AppScreen } from '../components/AppScreen';
import { STATES, getStateName } from '../data/states';

export function GamePage() {
  const { game, gameCode, myPlayerId, updateLocation, scorePlate, leaveGame } = useGame();
  const { location, startWatching, setManualState } = useLocation();
  const { isDark, toggle } = useTheme();

  const me = game?.players.find(p => p.id === myPlayerId);
  const isEnded = game?.status === 'ended';

  // Start GPS on mount
  useEffect(() => {
    startWatching();
  }, [startWatching]);

  // Send location updates to server when state changes
  useEffect(() => {
    if (location.state && game?.status === 'playing') {
      updateLocation(location.state, location.isGPS);
    }
  }, [location.state, location.isGPS, game?.status, updateLocation]);

  const handleScore = useCallback(
    (plateState: string) => {
      scorePlate(plateState);
    },
    [scorePlate]
  );

  const handleManualState = (e: ChangeEvent<HTMLSelectElement>) => {
    setManualState(e.target.value);
  };

  if (!game) return null;

  const effectiveState = location.state ?? me?.currentState ?? null;

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0">
        {game.isDemoMode && <DemoBanner />}

        {/* Nav bar */}
        <div className="flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-4">
          <button
            onClick={leaveGame}
            aria-label="Leave game"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink dark:text-atlas-ink-dark"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="text-xs font-semibold uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Game in Progress
          </span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark font-mono">{gameCode}</span>
            <ThemeToggle isDark={isDark} onToggle={toggle} />
          </div>
        </div>
      </div>

      <main className="flex-1 px-6 pt-1.5 pb-4 flex flex-col gap-4">
        {location.loading && (
          <div className="text-center text-sm text-atlas-ink-soft dark:text-atlas-ink-soft-dark py-2">
            Determining your location…
          </div>
        )}
        {location.error && !effectiveState && (
          <div className="bg-atlas-brick/10 dark:bg-atlas-brick-dark/15 border border-atlas-brick/40 dark:border-atlas-brick-dark/40 rounded-xl p-3 text-sm text-atlas-brick dark:text-atlas-brick-dark">
            {location.error}
          </div>
        )}

        {/* Manual state selector (always shown if no GPS state) */}
        {!location.isGPS && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-1">
              Set Your State Manually
            </label>
            <select
              value={effectiveState ?? ''}
              onChange={handleManualState}
              className="w-full px-3 py-2.5 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark text-sm"
            >
              <option value="">-- Select your state --</option>
              {STATES.map(s => (
                <option key={s.abbr} value={s.abbr}>
                  {s.abbr} — {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Current-state card */}
        {effectiveState && (
          <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl px-[18px] py-4 flex flex-col gap-1.5 shadow-sm">
            <span className="text-[11px] font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
              You Are In
            </span>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-serif text-[28px] font-bold text-atlas-ink dark:text-atlas-ink-dark leading-none">
                {getStateName(effectiveState)}
              </span>
              <span
                className={`text-xs font-semibold rounded-xl px-2.5 py-1 flex items-center gap-1 shrink-0 ${
                  location.isGPS
                    ? 'text-atlas-pine dark:text-atlas-pine-dark bg-atlas-pine/10 dark:bg-atlas-pine-dark/15'
                    : 'text-atlas-brick dark:text-atlas-brick-dark bg-atlas-brick/10 dark:bg-atlas-brick-dark/15'
                }`}
              >
                <svg viewBox="0 0 8 8" width="8" height="8" fill="currentColor"><circle cx="4" cy="4" r="4" /></svg>
                {location.isGPS ? 'GPS Located' : 'Manual'}
              </span>
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <ScoreBoard players={game.players} myPlayerId={myPlayerId} />

        {/* Plate selector */}
        {!isEnded && (
          <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl p-4 shadow-sm">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-4">
              Log a Plate
            </h2>
            <PlateSelector
              playerState={effectiveState}
              onScore={handleScore}
              disabled={!effectiveState}
            />
          </div>
        )}

        {isEnded && (
          <div className="text-center py-4 text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-sm">
            Game over! See the final scores above.
          </div>
        )}
      </main>
    </AppScreen>
  );
}
